import { useEffect, useState } from "react";
import { Cpu, Sparkles } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import MockBadge from "@/components/MockBadge";
import StatusPill from "@/components/StatusPill";
import { ops } from "@/lib/api";
import { fmtMs, fmtTime, shortId } from "@/lib/format";

export default function HermesRuntime() {
  const [status, setStatus] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [calls, setCalls] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const [s, t, c, sm] = await Promise.all([ops.hermesStatus(), ops.agentTasks(60), ops.llmCalls(60), ops.llmSummary()]);
        if (!alive) return;
        setStatus(s);
        setTasks(t);
        setCalls(c);
        setSummary(sm);
      } catch {/* ignore */}
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const seriesData = calls.slice(0, 30).reverse().map((c, i) => ({ x: i, ms: c.latency_ms, cost: c.cost_usd }));

  return (
    <div data-testid="page-hermes" className="p-6 md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="pj-label mb-2">· HERMES · Orchestrator</div>
          <h1 className="font-display text-[32px] sm:text-[40px] leading-[1.02]">Agent Runtime</h1>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <MockBadge label="Hybrid AI" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-px mb-6" style={{ background: "rgb(var(--ln))" }}>
        {["running","queued","succeeded","waiting_human","failed"].map((k) => (
          <div key={k} className="p-4" style={{ background: "rgb(var(--surface))" }} data-testid={`hermes-stat-${k}`}>
            <div className="pj-label">{k.replace("_"," ")}</div>
            <div className="pj-value-xl mt-2">{status?.[k] ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 pj-card">
          <div className="flex items-center justify-between p-4" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
            <h2 className="font-display text-[18px] flex items-center gap-2">
              <Cpu size={14} style={{ color: "rgb(var(--dim))" }} /> Recent agent tasks
            </h2>
            <span className="pj-label">live · 4s</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full mono text-[12px]">
              <thead style={{ color: "rgb(var(--dim))" }}>
                <tr className="text-left">
                  <th className="px-4 py-2 font-normal">Agent</th>
                  <th className="px-4 py-2 font-normal">State</th>
                  <th className="px-4 py-2 font-normal">Trip</th>
                  <th className="px-4 py-2 font-normal">Latency</th>
                  <th className="px-4 py-2 font-normal">Cost</th>
                  <th className="px-4 py-2 font-normal">When</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => (
                  <tr key={t.id} style={{ borderTop: "1px solid rgb(var(--ln))" }} data-testid={`hermes-task-${t.id}`}>
                    <td className="px-4 py-2.5" style={{ color: "rgb(var(--fg))" }}>
                      <span className="block max-w-[200px] truncate" title={t.agent}>{t.agent}</span>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap"><StatusPill state={t.state} /></td>
                    <td className="px-4 py-2.5 whitespace-nowrap" style={{ color: "rgb(var(--dim))" }}>{shortId(t.trip_request_id)}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{fmtMs(t.latency_ms)}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">${(t.cost_usd ?? 0).toFixed(4)}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap" style={{ color: "rgb(var(--muted-2))" }}>{fmtTime(t.created_at)}</td>
                  </tr>
                ))}
                {tasks.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8" style={{ color: "rgb(var(--dim))" }}>No agent tasks yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="pj-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-[18px] flex items-center gap-2"><Sparkles size={14} /> LLM Gateway</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 mono text-[11px]">
            <div className="pj-card p-3 min-w-0 overflow-hidden" style={{ background: "rgb(var(--surface-2))" }}>
              <div className="pj-label truncate">Calls</div>
              <div className="pj-value-lg mt-1 truncate">{summary?.total_calls ?? 0}</div>
            </div>
            <div className="pj-card p-3 min-w-0 overflow-hidden" style={{ background: "rgb(var(--surface-2))" }}>
              <div className="pj-label truncate">Total cost</div>
              <div className="pj-value-lg mt-1 truncate">${(summary?.total_cost_usd ?? 0).toFixed(4)}</div>
            </div>
            <div className="pj-card p-3 min-w-0 overflow-hidden" style={{ background: "rgb(var(--surface-2))" }}>
              <div className="pj-label truncate">Avg latency</div>
              <div className="pj-value-lg mt-1 truncate">{fmtMs(summary?.avg_latency_ms ?? 0)}</div>
            </div>
            <div className="pj-card p-3 min-w-0 overflow-hidden" style={{ background: "rgb(var(--surface-2))" }}>
              <div className="pj-label truncate">Tokens</div>
              <div className="pj-value-lg mt-1 truncate">{summary?.total_tokens ?? 0}</div>
            </div>
          </div>
          <div className="h-32 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={seriesData}>
                <XAxis dataKey="x" hide />
                <YAxis hide />
                <Tooltip contentStyle={{ background: "rgb(252,249,243)", border: "1px solid rgb(223,214,198)", color: "rgb(38,31,24)", fontSize: 11 }} labelStyle={{ color: "rgb(108,96,80)" }} />
                <Line type="monotone" dataKey="ms" stroke="rgb(169,130,28)" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="pj-label mt-2">Latency · last 30 calls</div>
        </section>
      </div>
    </div>
  );
}
